import { PlaybookDSL, PlaybookNode } from './schema';
import { LintIssue, LintResult } from './types';
import { extractTemplatePaths } from './template';
import { pageBranches, pageBranchTargets } from './page-branches';
import { ANSWER_CAPTURING_ELEMENT_TYPES } from './required-fields';

const ANSWER_CAPTURING = new Set<string>(ANSWER_CAPTURING_ELEMENT_TYPES);

export function lintPlaybook(playbook: PlaybookDSL): LintResult {
  const flow = playbook; // Alias for migration
  const issues: LintIssue[] = [];

  // Build available paths from variables (keyed by GUID)
  const availablePaths = new Set<string>();
  flow.variables.forEach((v) => availablePaths.add(`var:${v.id}`));

  // Build node ID set
  const nodeIds = new Set(flow.nodes.map((n) => n.id));

  // Track which nodes are reachable
  const reachableNodes = new Set<string>();
  const startNode = flow.nodes.find((n) => n.type === 'Start');

  if (!startNode) {
    issues.push({
      severity: 'error',
      message: 'Flow must have exactly one Start node',
    });
  } else {
    traverseReachable(startNode, flow.nodes, reachableNodes);
  }

  // Track variables written before they're read
  const writtenVars = new Set<string>();

  flow.nodes.forEach((node) => {
    // Check unreachable
    if (!reachableNodes.has(node.id) && node.type !== 'Start') {
      issues.push({
        severity: 'warning',
        nodeId: node.id,
        message: `Node "${node.id}" is unreachable`,
      });
    }

    // Check dangling next references
    const nexts = getNextIds(node);
    nexts.forEach((nextId) => {
      if (!nodeIds.has(nextId)) {
        issues.push({
          severity: 'error',
          nodeId: node.id,
          message: `References non-existent node "${nextId}"`,
        });
      }
    });

    // Branch options not pointed at a page yet. A warning, not an error: it is
    // a normal half-built state, but a viewer who picks one has nowhere to go.
    if (node.type === 'Page') {
      pageBranches(node)
        .filter((branch) => !branch.target)
        .forEach((branch) => {
          issues.push({
            severity: 'warning',
            nodeId: node.id,
            message:
              `Option "${branch.option}" of "${branch.elementLabel || 'this question'}" doesn't go to a page yet. ` +
              'Viewers who choose it will be stuck.',
          });
        });

      // Input elements with no bound variable silently discard every answer.
      // A warning (not an error) so flows in progress remain valid/saveable.
      const elements = (node as any).elements as Array<{
        id?: string;
        type?: string;
        label?: string;
        varId?: string;
      }> | undefined;
      if (Array.isArray(elements)) {
        const pageTitle = (node as any).title as string | undefined;
        elements
          .filter((el) => ANSWER_CAPTURING.has(el.type ?? '') && !el.varId)
          .forEach((el) => {
            const elLabel = el.label || el.id || 'unknown';
            issues.push({
              severity: 'warning',
              nodeId: node.id,
              message:
                `"${elLabel}" on page "${pageTitle ?? node.id}": answers won't be saved. ` +
                'Bind a variable to keep them.',
            });
          });
      }
    }

    // Check missing defaultNext on Switch
    if (node.type === 'Switch') {
      if (!node.defaultNext) {
        issues.push({
          severity: 'error',
          nodeId: node.id,
          message: 'Switch node must have defaultNext',
        });
      }
    }

    // Check templates and conditions
    const templates = getTemplatesFromNode(node);
    templates.forEach((tpl) => {
      const paths = extractTemplatePaths(tpl);
      paths.forEach((path) => {
        if (!availablePaths.has(path)) {
          issues.push({
            severity: 'error',
            nodeId: node.id,
            message: `Template references undefined path "${path}"`,
          });
        }
      });
    });

    // Check action dedupeKeyTemplate
    if (node.type === 'Action') {
      if (!node.action.dedupeKeyTemplate) {
        issues.push({
          severity: 'error',
          nodeId: node.id,
          message: 'Action node missing dedupeKeyTemplate',
        });
      }
    }

    // Check PII fields require preceding consent Note
    if (node.type === 'Form') {
      const hasPII = node.fields.some((f) => f.isPII);
      if (hasPII) {
        const hasConsentBefore = checkConsentBeforeNode(node.id, flow.nodes);
        if (!hasConsentBefore) {
          issues.push({
            severity: 'warning',
            nodeId: node.id,
            message: 'Form contains PII fields but no preceding consent Note found',
          });
        }
      }
    }

    // Track written variables (by GUID)
    if (node.type === 'SetVar') {
      writtenVars.add(`var:${node.varId}`);
      availablePaths.add(`var:${node.varId}`);
    }
    if (node.type === 'Prompt') {
      writtenVars.add(`var:${node.varId}`);
      availablePaths.add(`var:${node.varId}`);
    }
    if (node.type === 'Form') {
      node.fields.forEach((f) => {
        if (!f.varId) return;
        writtenVars.add(`var:${f.varId}`);
        availablePaths.add(`var:${f.varId}`);
      });
    }
  });

  return {
    valid: issues.filter((i) => i.severity === 'error').length === 0,
    issues,
  };
}

function traverseReachable(node: PlaybookNode, allNodes: PlaybookNode[], visited: Set<string>) {
  if (visited.has(node.id)) return;
  visited.add(node.id);

  const nexts = getNextIds(node);
  nexts.forEach((nextId) => {
    const nextNode = allNodes.find((n) => n.id === nextId);
    if (nextNode) {
      traverseReachable(nextNode, allNodes, visited);
    }
  });
}

function getNextIds(node: PlaybookNode): string[] {
  const nexts: string[] = [];
  if ('next' in node && node.next) nexts.push(node.next);
  if (node.type === 'If') {
    if (node.then) nexts.push(node.then);
    if (node.else) nexts.push(node.else);
  }
  if (node.type === 'Switch') {
    node.cases.forEach((c) => nexts.push(c.next));
    if (node.defaultNext) nexts.push(node.defaultNext);
  }
  // A page also leads wherever its branching options and buttons point.
  if (node.type === 'Page') pageBranchTargets(node).forEach((target) => nexts.push(target));
  return nexts;
}

function getTemplatesFromNode(node: PlaybookNode): string[] {
  const templates: string[] = [];

  if (node.type === 'Prompt' || node.type === 'Note') {
    templates.push(node.content);
  }
  if (node.type === 'SetVar') {
    templates.push(node.valueTemplate);
  }
  if (node.type === 'Action') {
    const action = node.action;
    templates.push(action.dedupeKeyTemplate);
    if (action.type === 'Email') {
      templates.push(action.to, action.subject, action.body);
    } else if (action.type === 'Webhook') {
      templates.push(action.url);
      if (action.bodyTemplate) templates.push(action.bodyTemplate);
    } else if (action.type === 'Sms') {
      templates.push(action.to, action.body);
    }
  }

  return templates;
}

function checkConsentBeforeNode(nodeId: string, allNodes: PlaybookNode[]): boolean {
  // Simple heuristic: check if there's any Note node that contains "consent" in content
  // In a real implementation, you'd traverse backwards in the graph
  const notesBeforeIndex = allNodes.findIndex((n) => n.id === nodeId);
  if (notesBeforeIndex === -1) return false;

  for (let i = 0; i < notesBeforeIndex; i++) {
    const n = allNodes[i];
    if (n.type === 'Note' && n.content.toLowerCase().includes('consent')) {
      return true;
    }
  }
  return false;
}

// Backwards compatibility alias
export const lintFlow = lintPlaybook;
